import { useState, useEffect } from "react";
import { Banknote, CreditCard, Plus, Trash2, Loader2 } from "lucide-react";
import { useI18n } from "@/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const n = (v) => Number(v || 0);

export function PaymentDialog({ open, onOpenChange, grossTotal, onComplete }) {
  const { t, fmtCurrency } = useI18n();
  const gross = n(grossTotal);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setRows([{ method: "cash", amount: gross.toFixed(2), tendered: gross.toFixed(2) }]);
  }, [open, grossTotal]); // eslint-disable-line

  const paid = rows.reduce((s, r) => s + n(r.amount), 0);
  const cashTendered = rows.filter((r) => r.method === "cash").reduce((s, r) => s + n(r.tendered || r.amount), 0);
  const noncash = rows.filter((r) => r.method !== "cash").reduce((s, r) => s + n(r.amount), 0);
  const cashOwed = Math.max(0, gross - noncash);
  const change = cashTendered > 0 ? Math.max(0, cashTendered - cashOwed) : 0;
  const remaining = Math.max(0, gross - paid);
  const canPay = paid + 1e-9 >= gross && rows.length > 0;

  const update = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, { method: "card", amount: remaining.toFixed(2), tendered: "" }]);

  const complete = async () => {
    setBusy(true);
    try {
      const payments = rows.map((r) => ({
        method: r.method, amount: String(r.amount || "0"),
        ...(r.method === "cash" && r.tendered ? { tendered: String(r.tendered) } : {}),
        ...(r.reference ? { reference: r.reference } : {}),
      }));
      await onComplete(payments);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pos-payment-dialog">
        <DialogHeader><DialogTitle>{t("pos.payment_title")}</DialogTitle></DialogHeader>
        <div className="flex items-baseline justify-between rounded-md bg-slate-900 px-4 py-3 text-white">
          <span className="text-sm opacity-80">{t("pos.total")}</span>
          <span className="text-2xl font-bold tabular-nums" data-testid="pos-payment-total">{fmtCurrency(gross)}</span>
        </div>
        <div className="space-y-3">
          {rows.map((r, i) => (
            <div key={i} className="flex items-end gap-2" data-testid={`pos-payment-row-${i}`}>
              <div className="w-32 space-y-1">
                <Label className="text-xs">{t("pos.method")}</Label>
                <Select value={r.method} onValueChange={(v) => update(i, { method: v })}>
                  <SelectTrigger data-testid={`pos-payment-method-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">{t("pos.cash")}</SelectItem>
                    <SelectItem value="card">{t("pos.card")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 space-y-1">
                <Label className="text-xs">{t("pos.amount")}</Label>
                <Input type="number" step="0.01" value={r.amount} onChange={(e) => update(i, { amount: e.target.value })}
                  className="tabular-nums" data-testid={`pos-payment-amount-${i}`} />
              </div>
              {r.method === "cash" ? (
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">{t("pos.tendered")}</Label>
                  <Input type="number" step="0.01" value={r.tendered} onChange={(e) => update(i, { tendered: e.target.value })}
                    className="tabular-nums" data-testid={`pos-payment-tendered-${i}`} />
                </div>
              ) : (
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">{t("pos.reference")}</Label>
                  <Input value={r.reference || ""} onChange={(e) => update(i, { reference: e.target.value })} data-testid={`pos-payment-ref-${i}`} />
                </div>
              )}
              {rows.length > 1 && (
                <Button variant="ghost" size="icon" onClick={() => setRows((rs) => rs.filter((_, idx) => idx !== i))} data-testid={`pos-payment-remove-${i}`}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              )}
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addRow} data-testid="pos-payment-add"><Plus className="mr-1 h-4 w-4" />{t("pos.split_payment")}</Button>
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="flex justify-between rounded bg-slate-100 px-3 py-2 dark:bg-slate-800"><span>{remaining > 0 ? t("pos.total") : t("pos.change_due")}</span>
            <b className="tabular-nums" data-testid="pos-payment-change">{fmtCurrency(remaining > 0 ? remaining : change)}</b></div>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="flex-1" onClick={() => update(0, { amount: gross.toFixed(2), tendered: gross.toFixed(2), method: "cash" })} data-testid="pos-payment-exact-cash"><Banknote className="mr-1 h-4 w-4" />{t("pos.cash")}</Button>
            <Button variant="outline" className="flex-1" onClick={() => update(0, { amount: gross.toFixed(2), method: "card" })} data-testid="pos-payment-exact-card"><CreditCard className="mr-1 h-4 w-4" />{t("pos.card")}</Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="pos-payment-cancel">{t("common.cancel")}</Button>
          <Button onClick={complete} disabled={busy || !canPay} data-testid="pos-payment-complete">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{t("pos.complete_sale")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
